import {
  AlertTriangle,
  Building2,
  Check,
  CheckCircle2,
  Copy,
  Database,
  Eye,
  EyeOff,
  Globe,
  Key,
  Layers,
  Phone,
  Plug,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Textarea from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";

const EMPTY_ENTERPRISE = {
  enterpriseNumber: "",
  enterpriseId: "",
  enterpriseToken: "",
  partnerAccountId: "",
  baseUrl: "https://api-staging.rocketpay.co.in",
  environment: "Sandbox",
};

interface DecodedToken {
  id?: string;
  mobile_number?: string;
  merchant_id?: string;
  role?: string;
  account_type?: string;
  source?: string;
  enterprise_id?: string;
  iat?: number;
  exp?: number;
}

export default function EnterpriseTab() {
  const toast = useToast();
  const [form, setForm] = useState({
    enterpriseNumber: EMPTY_ENTERPRISE.enterpriseNumber,
    enterpriseId: EMPTY_ENTERPRISE.enterpriseId,
    enterpriseToken: EMPTY_ENTERPRISE.enterpriseToken,
    partnerAccountId: EMPTY_ENTERPRISE.partnerAccountId,
    baseUrl: EMPTY_ENTERPRISE.baseUrl,
    environment: EMPTY_ENTERPRISE.environment,
  });

  const [isFromDatabase, setIsFromDatabase] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [lastVerified, setLastVerified] = useState("Not verified yet");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);

  // Decode JWT payload on token change
  useEffect(() => {
    if (form.enterpriseToken && form.enterpriseToken.includes(".")) {
      try {
        const parts = form.enterpriseToken.split(".");
        if (parts.length === 3) {
          const base64Url = parts[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
              .join("")
          );
          setDecodedToken(JSON.parse(jsonPayload));
          return;
        }
      } catch {
        setDecodedToken(null);
      }
    } else {
      setDecodedToken(null);
    }
  }, [form.enterpriseToken]);

  // Load from database on mount
  const fetchEnterpriseSettings = () => {
    setLoading(true);
    adminService
      .getEnterpriseSettings()
      .then((res) => {
        if (res?.data?.enterprise) {
          const ent = res.data.enterprise;
          setForm({
            enterpriseNumber: ent.enterpriseNumber || "",
            enterpriseId: ent.enterpriseId || "",
            enterpriseToken: ent.enterpriseToken || "",
            partnerAccountId: ent.partnerAccountId || "",
            baseUrl: ent.baseUrl || "https://api-staging.rocketpay.co.in",
            environment: ent.environment || "Sandbox",
          });
          setIsFromDatabase(Boolean(res.data.isFromDatabase));
          if (ent.lastUpdated || ent.updatedAt) {
            setLastUpdated(ent.lastUpdated || new Date(ent.updatedAt).toLocaleString("en-IN"));
          }
          if (ent.lastVerified) {
            setLastVerified(ent.lastVerified);
          }
        }
      })
      .catch(() => {
        // Fallback to general settings if separate route fails
        adminService
          .getSettings()
          .then((res) => {
            if (res?.data?.enterprise) {
              const ent = res.data.enterprise;
              setForm({
                enterpriseNumber: ent.enterpriseNumber || "",
                enterpriseId: ent.enterpriseId || "",
                enterpriseToken: ent.enterpriseToken || "",
                partnerAccountId: ent.partnerAccountId || "",
                baseUrl: ent.baseUrl || "https://api-staging.rocketpay.co.in",
                environment: ent.environment || "Sandbox",
              });
              if (ent.lastUpdated) setLastUpdated(ent.lastUpdated);
              if (ent.lastVerified) setLastVerified(ent.lastVerified);
              setIsFromDatabase(true);
            }
          })
          .catch(() => undefined);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEnterpriseSettings();
  }, []);

  // Save changes (creates if not exists, updates if exists)
  const handleSave = () => {
    setSaving(true);
    adminService
      .updateEnterpriseSettings(form)
      .then((res) => {
        setSaving(false);
        setIsFromDatabase(true);
        if (res?.data?.lastUpdated) {
          setLastUpdated(res.data.lastUpdated);
        } else {
          setLastUpdated(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
        }
        toast.success("Enterprise credentials saved to database successfully!");
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err.message || "Failed to save enterprise credentials");
      });
  };

  // Test enterprise connection and token validity
  const handleTestConnection = () => {
    setTesting(true);
    adminService
      .testEnterpriseConnection(form)
      .then((res) => {
        setTesting(false);
        if (res?.data?.lastVerified) {
          setLastVerified(res.data.lastVerified);
        }
        toast.success("Enterprise connection and token verified successfully!");
      })
      .catch((err) => {
        setTesting(false);
        toast.error(err.message || "Verification failed");
      });
  };

  // Quick copy helper
  const handleCopy = (key: string, text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopiedKey(key);
    toast.success("Copied to clipboard!");
    setTimeout(() => {
      setCopiedKey((prev) => (prev === key ? null : prev));
    }, 2000);
  };

  // Reset form to latest saved database values
  const handleResetToSaved = () => {
    fetchEnterpriseSettings();
    toast.info("Form reloaded with latest database settings");
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Information Banner with Database Status */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-[10px] border border-blue-200 bg-blue-50/60 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-blue-600">
            <Building2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-medium text-blue-900">
              Enterprise Configuration & Gateway Credentials
            </p>
            <p className="mt-0.5 text-xs text-blue-700">
              Stored directly in the database (<code className="font-mono text-blue-800">system_settings</code>).
              Changes automatically apply to mandate generation, merchant sessions, and order flows.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          {isFromDatabase ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 border border-emerald-200">
              <Database className="h-3.5 w-3.5 text-emerald-600" />
              Synced with Database
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 border border-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              Unsaved (Default Fallback)
            </span>
          )}
        </div>
      </div>

      {/* Main Credentials Card */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-line pb-4">
          <div>
            <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary-500" />
              Enterprise Credentials
            </h3>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              Configure your Enterprise Number, Enterprise ID, and JWT Token
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchEnterpriseSettings}
              loading={loading}
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Reload from Database
            </Button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* 1. Enterprise Number */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-ink flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-ink-secondary" />
                1. Enterprise Number
              </label>
              <button
                type="button"
                onClick={() => handleCopy("number", form.enterpriseNumber)}
                className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
              >
                {copiedKey === "number" ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-600">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <Input
              value={form.enterpriseNumber}
              onChange={(e) => setForm({ ...form, enterpriseNumber: e.target.value })}
              placeholder="+91XXXXXXXXXX"
              helper="Enterprise admin mobile number with country code"
            />
          </div>

          {/* 2. Enterprise ID */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-ink flex items-center gap-1.5">
                <Key className="h-3.5 w-3.5 text-ink-secondary" />
                2. Enterprise ID
              </label>
              <button
                type="button"
                onClick={() => handleCopy("id", form.enterpriseId)}
                className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
              >
                {copiedKey === "id" ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-600">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <Input
              value={form.enterpriseId}
              onChange={(e) => setForm({ ...form, enterpriseId: e.target.value })}
              placeholder="e.g. 01a0c994-5ce9-7166-9246-28c8b9fdbd7e"
              helper="UUID identifier assigned to your enterprise organization"
            />
          </div>

          {/* 3. Enterprise Token (Full Width) */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-ink flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-ink-secondary" />
                3. Enterprise Token (JWT)
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowToken((prev) => !prev)}
                  className="text-xs text-ink-secondary hover:text-ink flex items-center gap-1 cursor-pointer"
                >
                  {showToken ? (
                    <>
                      <EyeOff className="h-3 w-3" />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      <span>Show Full Token</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopy("token", form.enterpriseToken)}
                  className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
                >
                  {copiedKey === "token" ? (
                    <>
                      <Check className="h-3 w-3 text-emerald-600" />
                      <span className="text-emerald-600">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {showToken ? (
              <Textarea
                rows={4}
                value={form.enterpriseToken}
                onChange={(e) => setForm({ ...form, enterpriseToken: e.target.value })}
                placeholder="Paste your full JWT Enterprise Token here..."
                helper={`Token Length: ${form.enterpriseToken.length} characters`}
                className="font-mono text-xs break-all"
              />
            ) : (
              <div className="relative">
                <Input
                  type="password"
                  value={form.enterpriseToken}
                  onChange={(e) => setForm({ ...form, enterpriseToken: e.target.value })}
                  placeholder="Paste your full JWT Enterprise Token here..."
                  helper={`Token Length: ${form.enterpriseToken.length} characters (Protected view)`}
                />
              </div>
            )}
          </div>

          {/* 4. Partner Account ID */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-ink flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-ink-secondary" />
                Partner Account ID (Optional)
              </label>
              <button
                type="button"
                onClick={() => handleCopy("partner", form.partnerAccountId)}
                className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
              >
                {copiedKey === "partner" ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-600">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <Input
              value={form.partnerAccountId}
              onChange={(e) => setForm({ ...form, partnerAccountId: e.target.value })}
              placeholder="0000019a-2fa6-0e8c-8104-aa966d33b3f2"
              helper="Partner identifier for merchant account linking"
            />
          </div>

          {/* 5. API Base URL */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-ink flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-ink-secondary" />
                Gateway Base URL
              </label>
            </div>
            <Input
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              placeholder="https://api-staging.rocketpay.co.in"
              helper="API gateway URL for staging or production"
            />
          </div>
        </div>

        {/* Action Button Bar */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-line pt-4">
          <div className="text-xs text-ink-muted">
            {lastUpdated ? (
              <span>Last saved to database: {lastUpdated}</span>
            ) : (
              <span>Using default configuration</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleResetToSaved}
              disabled={loading || saving}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              Reset to Saved
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Configuration
            </Button>
          </div>
        </div>
      </div>

      {/* Decoded Token Payload Inspection */}
      {decodedToken && (
        <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
          <div className="flex items-center justify-between border-b border-line pb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <h4 className="text-sm font-semibold text-ink">Decoded Token Claims</h4>
                <p className="text-xs text-ink-secondary">Live validation from the provided JWT token</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Valid Structure (HS256)
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Role</span>
              <span className="text-sm font-semibold text-ink mt-0.5 block">{decodedToken.role || "N/A"}</span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Account Type</span>
              <span className="text-sm font-semibold text-ink mt-0.5 block">{decodedToken.account_type || "N/A"}</span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Mobile Number</span>
              <span className="text-sm font-semibold text-ink mt-0.5 block">{decodedToken.mobile_number || "N/A"}</span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Enterprise ID</span>
              <span className="text-xs font-mono text-ink mt-0.5 block truncate" title={decodedToken.enterprise_id}>
                {decodedToken.enterprise_id || "N/A"}
              </span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Merchant ID</span>
              <span className="text-xs font-mono text-ink mt-0.5 block truncate" title={decodedToken.merchant_id}>
                {decodedToken.merchant_id || "N/A"}
              </span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <span className="text-[11px] font-medium text-ink-muted uppercase tracking-wider block">Issued At</span>
              <span className="text-xs text-ink mt-0.5 block">
                {decodedToken.iat ? new Date(decodedToken.iat * 1000).toLocaleString("en-IN") : "N/A"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Environment & Connection Testing (Commented out)
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Environment & Connectivity</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">
          Configure runtime environment and test token authentication
        </p>

        <div className="mt-4">
          <span className="mb-2 block text-[13px] font-medium text-ink-secondary">Operating Mode</span>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              {
                id: "Sandbox",
                title: "Sandbox (Staging)",
                desc: "Connected to staging environment with test mandates",
              },
              {
                id: "Live",
                title: "Live Production",
                desc: "Real enterprise billing and NPCI e-mandate execution",
              },
            ].map((env) => (
              <button
                key={env.id}
                type="button"
                onClick={() => setForm({ ...form, environment: env.id })}
                className={`flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-colors cursor-pointer ${form.environment === env.id
                    ? "border-primary-500 bg-primary-50"
                    : "border-slate-300 bg-white hover:border-slate-400"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${form.environment === env.id ? "border-primary-500" : "border-slate-300"
                      }`}
                  >
                    {form.environment === env.id && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                  </span>
                  <span className="text-sm font-semibold text-ink">{env.title}</span>
                </div>
                <span className="text-xs text-ink-muted pl-6">{env.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-line pt-4">
          <Button
            variant="secondary"
            onClick={handleTestConnection}
            loading={testing}
            leftIcon={<Plug className="h-4 w-4" />}
          >
            Test Enterprise Session
          </Button>
          <p className="text-xs text-ink-muted">Last verified: {lastVerified}</p>
        </div>
      </div>
      */}
    </div>
  );
}
